import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../store/useStore";
import { createCheckout, getOrderStatus, cancelOrder } from "../lib/api";
import { ProgressStubs } from "../components/TicketStub";
import { cartTotal } from "../store/useStore";
import { t } from "../lib/i18n";
import { LANGUAGES } from "../data/languages";
const STEPS = ["step_1", "step_2", "step_3", "step_4"];
const STEP_ICONS = ["👉", "💛", "💳", "🟢"];
const PAY_IDLE_MS = 4 * 60_000;
export default function TerminalGuide() {
    const lang = useStore((s) => s.language);
    const cart = useStore((s) => s.cart);
    const customerName = useStore((s) => s.customerName);
    const setOrderId = useStore((s) => s.setOrderId);
    const orderId = useStore((s) => s.orderId);
    const setPaymentStatus = useStore((s) => s.setPaymentStatus);
    const navigate = useNavigate();
    const [stepIdx, setStepIdx] = useState(0);
    const [fail, setFail] = useState(null);
    const [showHelp, setShowHelp] = useState(false);
    const cancelTimer = useRef(null);
    const orderIdRef = useRef(null);
    const total = cartTotal(cart);
    const isRtl = LANGUAGES.find((l) => l.code === lang)?.rtl;
    const startOrder = async () => {
        setFail(null);
        setStepIdx(0);
        try {
            const r = await createCheckout({ cart, customerName, language: lang || "es" });
            setOrderId(r.order_id);
            orderIdRef.current = r.order_id;
        }
        catch {
            setFail("network");
        }
    };
    useEffect(() => { startOrder(); /* eslint-disable-next-line */ }, []);
    useEffect(() => {
        cancelTimer.current = setTimeout(() => {
            if (orderIdRef.current)
                cancelOrder(orderIdRef.current).catch(() => { });
            setFail("expired");
        }, PAY_IDLE_MS);
        return () => { if (cancelTimer.current)
            clearTimeout(cancelTimer.current); };
    }, [orderId]);
    useEffect(() => {
        if (!orderId || fail)
            return;
        let cancelled = false;
        const tick = async () => {
            if (cancelled)
                return;
            try {
                const s = await getOrderStatus(orderId);
                setPaymentStatus(s.status);
                if (s.status === "at_terminal")
                    setStepIdx((i) => Math.max(i, 1));
                if (s.status === "processed") {
                    navigate("/kiosk/thanks");
                    return;
                }
                if (["canceled", "expired", "rejected"].includes(s.status)) {
                    setFail(s.status);
                    return;
                }
            }
            catch { }
            setTimeout(tick, 1500);
        };
        tick();
        return () => { cancelled = true; };
    }, [orderId, fail, navigate, setPaymentStatus]);
    useEffect(() => {
        if (stepIdx < 1 || fail)
            return;
        const tm = setTimeout(() => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1)), 4000);
        return () => clearTimeout(tm);
    }, [stepIdx, fail]);
    const tryAgain = async () => {
        if (orderIdRef.current)
            cancelOrder(orderIdRef.current).catch(() => { });
        setOrderId(null);
        orderIdRef.current = null;
        await startOrder();
    };
    const editOrder = () => {
        if (orderIdRef.current)
            cancelOrder(orderIdRef.current).catch(() => { });
        setOrderId(null);
        navigate("/kiosk/menu");
    };
    if (showHelp) {
        return (_jsxs("div", { className: "grain min-h-screen flex flex-col items-center justify-center bg-paper p-8 text-center", dir: isRtl ? "rtl" : undefined, children: [_jsx("p", { className: "text-7xl mb-4", children: "\uD83D\uDE4B" }), _jsxs("p", { className: "font-hand text-3xl text-mezcal", children: ["\u2014 ", t(lang, "need_help"), " \u2014"] }), _jsx("h1", { className: "font-display font-black text-6xl mt-2 max-w-2xl tracking-kerned leading-tight", children: t(lang, "come_to_bar_title") }), _jsx("p", { className: "font-sans text-xl text-ink/60 mt-3", children: t(lang, "come_to_bar_sub") }), _jsxs("button", { onClick: () => setShowHelp(false), className: "mt-12 px-10 py-4 rounded-sm bg-ink text-paper font-display font-bold", children: ["\u2190 ", t(lang, "guide_help_back")] })] }));
    }
    if (fail) {
        const failKey = {
            canceled: "fail_canceled", expired: "fail_expired",
            rejected: "fail_rejected", network: "fail_network",
        }[fail];
        return (_jsxs("div", { className: "grain min-h-screen flex flex-col items-center justify-center bg-paper p-8 text-center", dir: isRtl ? "rtl" : undefined, children: [_jsxs("p", { className: "font-hand text-3xl text-cactus", children: ["\u2014 ", t(lang, "fail_caption"), " \u2014"] }), _jsx("h1", { className: "font-display font-black text-6xl mt-2 max-w-2xl tracking-kerned leading-tight", children: t(lang, failKey) }), _jsx("p", { className: "font-sans text-xl text-ink/60 mt-4", children: t(lang, "fail_no_charge") }), _jsxs("div", { className: "mt-12 grid grid-cols-2 gap-4 w-full max-w-xl", children: [_jsx("button", { onClick: editOrder, className: "py-5 rounded-sm border-2 border-ink/40 hover:border-ink font-display font-bold text-lg", children: t(lang, "fail_edit") }), _jsx("button", { onClick: tryAgain, className: "py-5 rounded-sm bg-mezcal text-paper font-display font-bold text-lg", children: t(lang, "fail_try_again") })] }), _jsx("button", { onClick: () => setShowHelp(true), className: "mt-8 font-mono text-xs tracking-widest text-ink/50 underline", children: t(lang, "need_help") })] }));
    }
    const stepKey = STEPS[stepIdx];
    const stepIcon = STEP_ICONS[stepIdx];
    return (_jsxs("div", { className: "grain min-h-screen flex flex-col bg-ink text-paper p-10 relative", dir: isRtl ? "rtl" : undefined, children: [_jsxs("header", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("p", { className: "font-mono text-[10px] tracking-[0.4em] text-mango uppercase", children: t(lang, "guide_in_progress") }), _jsx("h2", { className: "font-display font-black text-2xl mt-1", children: customerName })] }), _jsx(ProgressStubs, { total: STEPS.length, current: stepIdx })] }), _jsxs("div", { className: "flex-1 flex flex-col items-center justify-center text-center", children: [_jsx("div", { className: "text-[10rem] leading-none mb-6 animate-breathe", children: stepIcon }), _jsxs("p", { className: "font-mono text-xs tracking-[0.4em] text-mango uppercase", children: [t(lang, "guide_step_word"), " ", stepIdx + 1, " / ", STEPS.length] }), _jsx("h1", { className: "font-display font-black text-7xl mt-3 max-w-3xl tracking-kerned leading-[0.95]", children: t(lang, stepKey) })] }), _jsxs("footer", { className: "flex items-end justify-between", children: [_jsxs("div", { className: "font-mono text-xs tracking-widest text-paper/50", children: [t(lang, "guide_order"), " \u00B7 ", orderId ? orderId.slice(-6).toUpperCase() : "·····"] }), _jsxs("div", { className: "font-display text-3xl font-bold tabular-nums text-mango", children: ["$", total] }), _jsxs("div", { className: "flex gap-5 text-paper/60 font-mono text-xs tracking-widest", children: [_jsx("button", { onClick: editOrder, className: "underline", children: t(lang, "guide_cancel") }), _jsx("span", { children: "\u00B7" }), _jsx("button", { onClick: () => setShowHelp(true), className: "underline", children: t(lang, "guide_help") })] })] })] }));
}
